import { useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { FileSpreadsheet, Download, Search, Loader2 } from 'lucide-react';

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

const StudentFeesExport = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  const openReport = async () => {
    setOpen(true);
    setLoading(true);
    setSearch('');
    try {
      const res = await api.get('/payments/student-fees-report');
      setReport(res.data);
      setSelected(new Set((res.data.students || []).map((s) => s.student_name)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load fees report');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const students = report?.students || [];
  const classesLabel = (s) =>
    (s.classes || []).map((c) => `${c.class_name} (£${Number(c.fee).toFixed(0)})`).join(' · ');
  const q = search.trim().toLowerCase();
  const visible = q
    ? students.filter((s) =>
        s.student_name.toLowerCase().includes(q) ||
        (s.classes || []).some((c) => (c.class_name || '').toLowerCase().includes(q)))
    : students;
  const allVisibleSelected = visible.length > 0 && visible.every((s) => selected.has(s.student_name));

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((s) => next.delete(s.student_name));
      else visible.forEach((s) => next.add(s.student_name));
      return next;
    });
  };

  const selectedRows = students.filter((s) => selected.has(s.student_name));
  const selTotal = selectedRows.reduce((a, s) => a + (s.fee_total || 0), 0);
  const selAvg = selectedRows.length ? selTotal / selectedRows.length : 0;

  const exportCsv = () => {
    if (!selectedRows.length) {
      toast.error('Select at least one student');
      return;
    }
    const lines = [
      ['Student', 'Classes (fee per 6-week block)', 'Discount', '6-Week Block Fee (GBP)'].map(csvCell).join(','),
      ...selectedRows.map((s) =>
        [
          s.student_name,
          classesLabel(s),
          s.discount_percent ? `${s.discount_percent}%` : '',
          s.fee_total.toFixed(2),
        ].map(csvCell).join(',')
      ),
      '',
      [csvCell('TOTAL DUE PER BLOCK'), csvCell(`${selectedRows.length} students`), csvCell(''), csvCell(selTotal.toFixed(2))].join(','),
      [csvCell('AVERAGE PER STUDENT'), csvCell(''), csvCell(''), csvCell(selAvg.toFixed(2))].join(','),
    ];
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student-fees-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedRows.length} students`);
  };

  return (
    <>
      <Card className="bg-[#111] border-[#222]" data-testid="student-fees-export-card">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
            Student Fees Report
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-white/50">
            What each student's classes equate to per 6-week block (the amount due to come in),
            with total and average. Choose exactly who goes on the list.
          </p>
          <Button
            onClick={openReport}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
            data-testid="open-fees-report-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Open Report
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
              Student Fees Report
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-white/30" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="w-5 h-5 accent-emerald-500 cursor-pointer"
                    data-testid="fees-select-all"
                  />
                  <span className="text-sm text-white/70">Select all ({visible.length})</span>
                </label>
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search student or class..."
                    className="pl-9 h-9 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/30"
                    data-testid="fees-search-input"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto space-y-1 pr-1 min-h-0" data-testid="fees-student-list">
                {visible.length === 0 ? (
                  <p className="text-white/40 text-sm py-6 text-center">No students match</p>
                ) : (
                  visible.map((s) => (
                    <label
                      key={s.student_name}
                      className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer ${
                        selected.has(s.student_name)
                          ? 'bg-emerald-500/5 border-emerald-500/20'
                          : 'bg-[#0a0a0a] border-[#222]'
                      }`}
                      data-testid={`fees-row-${s.student_name.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={selected.has(s.student_name)}
                          onChange={() => toggle(s.student_name)}
                          className="w-5 h-5 accent-emerald-500 cursor-pointer shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="text-sm text-white font-medium">
                            {s.student_name}
                            {s.discount_percent > 0 && (
                              <span className="ml-2 text-[10px] text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
                                {s.discount_percent}% discount
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-white/40 truncate">{classesLabel(s) || 'No classes listed'}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-white shrink-0">£{s.fee_total.toFixed(2)}<span className="text-white/40 font-normal text-xs"> /block</span></span>
                    </label>
                  ))
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-[#222] pt-3">
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid="fees-selected-count">
                  {selectedRows.length} selected
                </Badge>
                <Badge className="bg-white/10 text-white border-white/20" data-testid="fees-total">
                  Total due per block £{selTotal.toFixed(2)}
                </Badge>
                <Badge className="bg-white/10 text-white border-white/20" data-testid="fees-average">
                  Average £{selAvg.toFixed(2)}
                </Badge>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="border-[#333] text-white hover:bg-[#222]"
                >
                  Close
                </Button>
                <Button
                  onClick={exportCsv}
                  disabled={!selectedRows.length}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  data-testid="export-fees-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV ({selectedRows.length})
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudentFeesExport;
